// Rules of a loop (SPEC §3.4, §6.1): what the locked editor allows and the validator enforces.
// The editor *keeps* loops inside these rules (clamping, key lock); the server validator
// (server/src/checks) *rejects* anything outside them. Numbers mirror `engine/src/song.rs`.

import { LOOP_TICKS, MAX_PATTERNS, SECTIONS } from "./clock";
import type { LoopData } from "./generated/LoopData";
import type { NoteEvent } from "./generated/NoteEvent";
import type { DriveKind } from "./generated/DriveKind";
import type { InsertFilterKind } from "./generated/InsertFilterKind";
import type { Inserts } from "./generated/Inserts";
import type { SoundMix } from "./generated/SoundMix";
import { inKey, snapToKey, type Key } from "./music";
import {
  ATMOS1_LANE_MIX,
  FIRST_ATMOS,
  NUM_INSTRUMENTS,
  NUM_LANE_SOUNDS,
  NUM_PADS,
  NUM_SOUNDS,
  OLD_LANE_MIX,
  slotRange,
} from "./period";
import { patternCount, sections } from "./song";

export const LIMITS = {
  maxEvents: 1024,
  minNotes: 8,
  minSounds: 2,
  maxDrumNotesPerBarPerPad: 32,
  maxSimultaneousPerInstrument: 6,
  maxSimultaneousVoices: 24,
  titleMaxLength: 40,
} as const;

export const MIX_RANGE = {
  volume: [0, 1],
  pan: [-1, 1],
  cutoff: [150, 20000],
  reverb_send: [0, 0.6],
  delay_send: [0, 0.5],
} as const satisfies Record<Exclude<keyof SoundMix, "inserts">, readonly [number, number]>;

/** Insert effects per sound (engine `Inserts`): a drive, then a filter, each with an amount 0–1. */
export const DRIVE_KINDS: readonly { value: DriveKind; label: string; hint: string }[] = [
  { value: "off", label: "Off", hint: "" },
  { value: "tape", label: "Tape", hint: "Soft, warm saturation" },
  { value: "tube", label: "Tube", hint: "Rounder saturation with even harmonics" },
  { value: "crunch", label: "Crunch", hint: "Overdrive with a tone filter against fizz" },
  { value: "lofi", label: "Lo-fi", hint: "Gentle bit and sample-rate reduction" },
];
export const INSERT_FILTER_KINDS: readonly { value: InsertFilterKind; label: string; hint: string }[] = [
  { value: "off", label: "Off", hint: "" },
  { value: "highpass", label: "High-pass", hint: "Thins the sound from below (40 Hz → 2 kHz)" },
  { value: "bandpass", label: "Band-pass", hint: "Keeps one band, telephone style (250 Hz → 4 kHz)" },
  { value: "wobble", label: "Wobble", hint: "A low-pass that opens and closes once per beat" },
];
const DRIVE_VALUES = new Set<string>(DRIVE_KINDS.map((d) => d.value));
const FILTER_VALUES = new Set<string>(INSERT_FILTER_KINDS.map((d) => d.value));

/** Clamp amounts to 0–1; `undefined` when nothing is switched on. Mirrors `Inserts::clamped`. */
export function clampInserts(i: Inserts | undefined): Inserts | undefined {
  if (!i) return undefined;
  const drive = DRIVE_VALUES.has(i.drive) ? i.drive : "off";
  const filter = FILTER_VALUES.has(i.filter) ? i.filter : "off";
  if (drive === "off" && filter === "off") return undefined;
  return {
    drive,
    drive_amount: clamp(i.drive_amount, 0, 1),
    filter,
    filter_amount: clamp(i.filter_amount, 0, 1),
  };
}

function insertsValid(i: Inserts | undefined): boolean {
  if (i === undefined) return true;
  return (
    DRIVE_VALUES.has(i.drive) &&
    FILTER_VALUES.has(i.filter) &&
    [i.drive_amount, i.filter_amount].every((v) => Number.isFinite(v) && v >= 0 && v <= 1)
  );
}

const clamp = (v: number, lo: number, hi: number) => (Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : lo);

export function clampMix(m: SoundMix): SoundMix {
  return {
    volume: clamp(m.volume, ...MIX_RANGE.volume),
    pan: clamp(m.pan, ...MIX_RANGE.pan),
    cutoff: Number.isFinite(m.cutoff) ? clamp(m.cutoff, ...MIX_RANGE.cutoff) : MIX_RANGE.cutoff[1],
    reverb_send: clamp(m.reverb_send, ...MIX_RANGE.reverb_send),
    delay_send: clamp(m.delay_send, ...MIX_RANGE.delay_send),
    ...(clampInserts(m.inserts) ? { inserts: clampInserts(m.inserts) } : {}),
  };
}

export function mixInRange(m: SoundMix): boolean {
  return (
    (Object.keys(MIX_RANGE) as (keyof typeof MIX_RANGE)[]).every(
      (k) => Number.isFinite(m[k]) && m[k] >= MIX_RANGE[k][0] && m[k] <= MIX_RANGE[k][1],
    ) && insertsValid(m.inserts)
  );
}

/** An instrument slot (16–19) or an extra instrument lane (32–39) — pitched, unlike drum and
 *  atmos pads. */
export const isInstrumentSlot = (pad: number) =>
  (pad >= NUM_PADS && pad < FIRST_ATMOS) || (pad >= NUM_SOUNDS && pad < NUM_LANE_SOUNDS);

/** Instrument variants: 0 = the day's main sound, 1–2 = its alternates. */
export const MAX_VARIANT = 2;

/** A loop's instrument variants are valid (absent, or one integer 0–2 per instrument). */
export function variantsValid(v: LoopData["variants"]): boolean {
  return (
    v === undefined ||
    (v.length <= NUM_INSTRUMENTS && v.every((x) => Number.isInteger(x) && x >= 0 && x <= MAX_VARIANT))
  );
}

/** The variant instrument `slot` plays in `loop` (0 when unset or invalid). */
export function loopVariant(loop: Pick<LoopData, "variants">, slot: number): number {
  if (!isInstrumentSlot(slot)) return 0;
  const v = loop.variants?.[slot - NUM_PADS] ?? 0;
  return Number.isInteger(v) && v >= 0 && v <= MAX_VARIANT ? v : 0;
}

/** Key lock: snap an instrument note into the period's scale and the slot's playable range. */
export function keyLockNote(note: number, slot: number, key: Key): number {
  const range = slotRange(slot);
  let n = snapToKey(Math.round(note), key);
  if (!range) return n;
  while (n < range.lo) n += 12;
  while (n > range.hi) n -= 12;
  if (n < range.lo) n = snapToKey(range.lo + 1, key);
  return n;
}

/** Is this note allowed for this slot in this key? */
export function noteAllowed(note: number, slot: number, key: Key): boolean {
  if (!isInstrumentSlot(slot)) return true;
  const range = slotRange(slot)!;
  return inKey(note, key) && note >= range.lo && note <= range.hi;
}

/** Bring an event inside the rules (key lock, ranges); null if it can't be (bad sound/tick). */
export function sanitizeEvent(e: NoteEvent, key: Key): NoteEvent | null {
  if (!Number.isInteger(e.pad) || e.pad < 0 || e.pad >= NUM_LANE_SOUNDS) return null;
  const tick = Math.round(e.tick);
  if (!(tick >= 0 && tick < LOOP_TICKS)) return null;
  return {
    tick,
    pad: e.pad,
    note: isInstrumentSlot(e.pad) ? keyLockNote(e.note, e.pad, key) : 60,
    velocity: clamp(Math.round(e.velocity), 1, 127),
    length_ticks: clamp(Math.round(e.length_ticks), 1, LOOP_TICKS - tick),
  };
}

export function sortEvents(events: NoteEvent[]): NoteEvent[] {
  return events.sort((a, b) => a.tick - b.tick || a.pad - b.pad || a.note - b.note);
}

/** One pattern brought inside the rules: valid events, deduplicated, ≤ max, sorted. */
export function sanitizePattern(raw: NoteEvent[], key: Key): NoteEvent[] {
  const seen = new Set<string>();
  const events: NoteEvent[] = [];
  for (const r of raw) {
    const e = sanitizeEvent(r, key);
    if (!e) continue;
    const k = `${e.pad}:${e.note}:${e.tick}`;
    if (seen.has(k)) continue;
    seen.add(k);
    events.push(e);
    if (events.length >= LIMITS.maxEvents) break;
  }
  return sortEvents(events);
}

/** A song brought fully inside the rules: valid patterns (≤ 4), arrangement, clamped mix. */
/**
 * Songs saved in an older layout with extra lanes (recognised by their mix length; only they had
 * those lengths) move to today's: alternates on 32–39, the atmos kit's pads on 20–31.
 * * 28 entries (before atmos): lanes 20–27 → 32–39.
 * * 31 entries (one atmos slot): atmos 20 stays (the kit's first pad), its alternates 29–30 →
 *   pads 21–22, lanes 21–28 → 32–39.
 * Mix entries move with their sounds; new pads take `defaults` (the period's). Idempotent (the
 * result has a 40-entry mix); the engine does the same (`LoopData::upgraded`).
 */
export function upgradeLoop(loop: LoopData, defaults: SoundMix[] = []): LoopData {
  const atmos1 = loop.mix.length === ATMOS1_LANE_MIX;
  if (!atmos1 && loop.mix.length !== OLD_LANE_MIX) return loop;
  const map = (p: number) =>
    atmos1 ? (p >= 21 && p <= 28 ? p + 11 : p === 29 || p === 30 ? p - 8 : p) : p >= 20 && p < 28 ? p + 12 : p;
  const shift = (events: NoteEvent[]) => events.map((e) => ({ ...e, pad: map(e.pad) }));
  const fallback: SoundMix = { volume: 0.3, pan: 0, cutoff: 20000, reverb_send: 0.4, delay_send: 0.1 };
  const mix = Array.from({ length: NUM_LANE_SOUNDS }, (_, i) => defaults[i] ?? fallback);
  loop.mix.forEach((m, i) => {
    if (!(atmos1 && (i === 29 || i === 30))) mix[map(i)] = m;
  });
  const out: LoopData = { ...loop, events: shift(loop.events), mix };
  if (loop.extra_patterns) out.extra_patterns = loop.extra_patterns.map(shift);
  if (loop.lanes) out.lanes = loop.lanes.map(map).filter((p) => p >= NUM_SOUNDS);
  return out;
}

export function sanitizeLoop(loop: LoopData, key: Key, defaultMix: SoundMix[]): LoopData {
  loop = upgradeLoop(loop, defaultMix);
  const extra = (loop.extra_patterns ?? []).slice(0, MAX_PATTERNS - 1).map((p) => sanitizePattern(p, key));
  const events = sanitizePattern(loop.events, key);
  // Extra lanes: the ones the song added plus any its notes use.
  const lanes = new Set(
    (loop.lanes ?? []).filter((p) => Number.isInteger(p) && p >= NUM_SOUNDS && p < NUM_LANE_SOUNDS),
  );
  for (const p of [events, ...extra]) for (const e of p) if (e.pad >= NUM_SOUNDS) lanes.add(e.pad);
  const mix = Array.from({ length: lanes.size ? NUM_LANE_SOUNDS : NUM_SOUNDS }, (_, i) =>
    clampMix(loop.mix[i] ?? defaultMix[i] ?? { volume: 0.8, pan: 0, cutoff: 20000, reverb_send: 0, delay_send: 0 }),
  );
  const out: LoopData = { events, mix };
  if (loop.alt_key === true) out.alt_key = true;
  if (lanes.size) out.lanes = [...lanes].sort((a, b) => a - b);
  const variants = Array.from({ length: NUM_INSTRUMENTS }, (_, i) => loopVariant(loop, NUM_PADS + i));
  if (variants.some((v) => v !== 0)) out.variants = variants;
  if (extra.length) {
    out.extra_patterns = extra;
    out.arrangement = sections({ extra_patterns: extra, arrangement: loop.arrangement });
  }
  return out;
}

/** Song-level structure is valid: ≤ 4 patterns, 4 sections that refer to existing patterns. */
export function arrangementValid(loop: Pick<LoopData, "extra_patterns" | "arrangement">): boolean {
  const n = patternCount(loop);
  if ((loop.extra_patterns?.length ?? 0) > MAX_PATTERNS - 1) return false;
  const a = loop.arrangement;
  return a === undefined || (a.length === SECTIONS && a.every((p) => Number.isInteger(p) && p >= 0 && p < n));
}
